/**
 * Constraint kind registry (Faz 6.3/6.4): display names, glyph symbols,
 * expected reference shapes and the approximate DOF-depth table used for
 * the first-version freedom estimate (full redundancy diagnosis is a
 * Faz 10 note, matching the plan).
 */

import type { DimensionalKind, GeometricKind } from './types';

export type ConstraintKind = GeometricKind | DimensionalKind;

export const DIMENSIONAL_KINDS: ReadonlySet<string> = new Set<DimensionalKind>([
    'Distance', 'DistanceX', 'DistanceY', 'Angle', 'Radius', 'Diameter',
]);

export const isDimensionalKind = (kind: string): kind is DimensionalKind =>
    DIMENSIONAL_KINDS.has(kind);

/** Glyph hap label (OCS symbolic labels: ≡ ∈ G² F │ …). */
export const KIND_GLYPH: Record<ConstraintKind, string> = {
    Coincident: '≡',
    Horizontal: '─',
    Vertical: '│',
    Parallel: '∥',
    Perpendicular: '⊥',
    Tangent: '○',
    Smooth: 'G²',
    Concentric: '◎',
    Equal: '=',
    Fixed: 'F',
    PointOnCurve: '∈',
    Symmetric: '⟷',
    Midpoint: '△',
    Colinear: '⟼',
    Distance: '⇤',
    DistanceX: '↔',
    DistanceY: '↕',
    Angle: '∠',
    Radius: 'R',
    Diameter: 'Ø',
};

/** i18n key suffix under `constraints.kind.*`. */
export const KIND_LABEL_KEY: Record<ConstraintKind, string> = {
    Coincident: 'coincident',
    Horizontal: 'horizontal',
    Vertical: 'vertical',
    Parallel: 'parallel',
    Perpendicular: 'perpendicular',
    Tangent: 'tangent',
    Smooth: 'smooth',
    Concentric: 'concentric',
    Equal: 'equal',
    Fixed: 'fixed',
    PointOnCurve: 'pointOnCurve',
    Symmetric: 'symmetric',
    Midpoint: 'midpoint',
    Colinear: 'colinear',
    Distance: 'distance',
    DistanceX: 'distanceX',
    DistanceY: 'distanceY',
    Angle: 'angle',
    Radius: 'radius',
    Diameter: 'diameter',
};

/** Accepted entity types per kind (pick validation). Rectangle participates
 *  only where its two writable corners suffice — its other corners are
 *  derived, so segment kinds accept lines/polylines/polygons. */
export const KIND_ACCEPT_TYPES: Record<ConstraintKind, string[]> = {
    Coincident: ['line', 'polyline', 'polygon', 'rectangle', 'arc', 'circle', 'ellipse', 'curve'],
    Horizontal: ['line', 'polyline', 'polygon'],
    Vertical: ['line', 'polyline', 'polygon'],
    Parallel: ['line', 'polyline', 'polygon'],
    Perpendicular: ['line', 'polyline', 'polygon'],
    Tangent: ['line', 'polyline', 'circle', 'arc'],
    Smooth: ['curve', 'spline', 'nurbs-curve', 'polyline'],
    Concentric: ['circle', 'arc', 'ellipse'],
    Equal: ['line', 'polyline', 'polygon', 'circle', 'arc'],
    Fixed: ['line', 'polyline', 'polygon', 'rectangle', 'arc', 'circle', 'ellipse', 'curve'],
    PointOnCurve: ['line', 'polyline', 'polygon', 'circle', 'arc', 'ellipse', 'curve'],
    Symmetric: ['line', 'polyline', 'polygon', 'arc', 'circle', 'ellipse'],
    Midpoint: ['line', 'polyline', 'polygon', 'arc', 'circle', 'ellipse'],
    Colinear: ['line', 'polyline', 'polygon'],
    Distance: ['line', 'polyline', 'polygon', 'circle', 'arc'],
    DistanceX: ['line', 'polyline', 'polygon'],
    DistanceY: ['line', 'polyline', 'polygon'],
    Angle: ['line', 'polyline', 'polygon'],
    Radius: ['circle', 'arc'],
    Diameter: ['circle', 'arc'],
};

/**
 * Approximate DOF depth per kind (first-version estimate; entity geometry
 * contributes lines 4 / circles 3 / arcs 5 free parameters — OCS §2.2 —
 * and each satisfied constraint removes this many).
 */
export const KIND_DOF_DEPTH: Record<ConstraintKind, number> = {
    Coincident: 2,
    Horizontal: 1,
    Vertical: 1,
    Parallel: 1,
    Perpendicular: 1,
    Tangent: 1,
    Smooth: 2,
    Concentric: 2,
    Equal: 1,
    Fixed: 2,
    PointOnCurve: 1,
    Symmetric: 2,
    Midpoint: 2,
    Colinear: 2,
    Distance: 1,
    DistanceX: 1,
    DistanceY: 1,
    Angle: 1,
    Radius: 1,
    Diameter: 1,
};
